// frontend/src/pages/MetasPage/MetasPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Layout,
    Typography,
    Form,
    Select,
    InputNumber,
    Input,
    Button,
    Row,
    Col,
    Card,
    Progress,
    Statistic,
    Spin,
    Alert,
    notification,
    message,
    Popconfirm,
    Modal,
    Drawer,
    Segmented,
    Space,
    Tag,
    List,
    Tooltip,
    Tabs,
    DatePicker,
} from 'antd';
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    SaveOutlined,
    InfoCircleOutlined,
    EyeOutlined,
    SwapOutlined,
} from '@ant-design/icons';
import MobileLaunchSheet from '../../components/MobileLaunchSheet/MobileLaunchSheet';
import { getMobileFullscreenModalProps } from '../../utils/mobileModal';
import {
    getMetasComProgresso,
    getDetalheMetaProgresso,
    createOrUpdateMeta,
    deleteMeta,
    getMetasPoupancaComProgresso,
    createOrUpdateMetaPoupanca,
    deleteMetaPoupanca,
    registrarAporteMetaPoupanca,
    registrarResgateMetaPoupanca,
    getSugestaoReserva,
} from '../../services/metaService';
import { getCategorias } from '../../services/financeService';
import { getContas } from '../../services/accountService';
import SeletorDeMes from '../../components/SeletorDeMes/SeletorDeMes';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import { useNavigate, useSearchParams } from 'react-router-dom';

/** Normaliza lista da API (array puro ou paginado com `results`). */
function asList(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
}

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const REGIME_STORAGE_KEY = 'financeiro.regime';
const REGIME_LEGACY_KEY = 'metas.regime';
const REGIME_COMPETENCIA = 'competencia';
const REGIME_FATURA = 'fatura';
const ABA_STORAGE_KEY = 'financeiro.metas.aba';

const REGIME_OPTIONS = [
    { label: 'Quando gastei', value: REGIME_COMPETENCIA },
    { label: 'Quando a fatura vence', value: REGIME_FATURA },
];

const REGIME_HELP = {
    [REGIME_COMPETENCIA]:
        'Conta o que você gastou neste mês (data da compra), mesmo que o cartão vença depois.',
    [REGIME_FATURA]:
        'Conta pelo vencimento da fatura / quando o compromisso cai no mês.',
};

const TIPO_OBJETIVO = 'objetivo';
const TIPO_RESERVA = 'reserva_emergencia';

const TIPO_CONTA_LABEL = {
    conta_corrente: 'Conta Corrente',
    poupanca: 'Poupança',
    investimento: 'Investimento',
    carteira: 'Carteira',
    outros: 'Outros',
};

function labelTipoConta(tipo) {
    return TIPO_CONTA_LABEL[tipo] || String(tipo || '').replace(/_/g, ' ');
}

function formatBRL(value) {
    const n = Number(value) || 0;
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Máscara BRL: usuário digita só números; sistema formata (12.000,00). */
function formatMoneyInput(value) {
    if (value === undefined || value === null || value === '') return '';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
}

function parseMoneyMask(value) {
    if (!value) return '';
    const onlyNumbers = String(value).replace(/[^\d]/g, '');
    if (!onlyNumbers) return '';
    return parseInt(onlyNumbers, 10) / 100;
}

const moneyInputNumberProps = {
    style: { width: '100%' },
    precision: 2,
    step: 100,
    min: 0.01,
    size: 'large',
    formatter: formatMoneyInput,
    parser: parseMoneyMask,
};

function formatDataCurta(value) {
    if (!value) return '—';
    const d = dayjs(value);
    return d.isValid() ? d.format('DD/MM/YYYY') : String(value);
}

function getStatusMeta(percentual) {
    if (percentual > 100) return { label: 'Estourou', color: 'error' };
    if (percentual > 90) return { label: 'Crítico', color: 'error' };
    if (percentual > 70) return { label: 'Atenção', color: 'warning' };
    return { label: 'Sob controle', color: 'success' };
}

function getStatusPoupanca(percentual, atingida) {
    if (atingida || percentual >= 100) return { label: 'Atingida', color: 'success' };
    if (percentual >= 70) return { label: 'No caminho', color: 'processing' };
    if (percentual >= 30) return { label: 'Em andamento', color: 'warning' };
    return { label: 'Começando', color: 'default' };
}

function getProgressStroke(percentual) {
    if (percentual > 90) return '#D64545';
    if (percentual > 70) return '#B89040';
    return undefined;
}

function getPoupancaStroke(percentual) {
    if (percentual >= 100) return '#2F9E6F';
    if (percentual >= 70) return '#50B0A0';
    if (percentual >= 30) return '#B89040';
    return undefined;
}

function loadRegimePreferido() {
    try {
        const saved = localStorage.getItem(REGIME_STORAGE_KEY);
        if (saved === REGIME_COMPETENCIA || saved === REGIME_FATURA) return saved;
        const legacy = localStorage.getItem(REGIME_LEGACY_KEY);
        if (legacy === REGIME_COMPETENCIA || legacy === REGIME_FATURA) {
            localStorage.setItem(REGIME_STORAGE_KEY, legacy);
            return legacy;
        }
    } catch (_) {
        /* ignore */
    }
    return REGIME_COMPETENCIA;
}

function loadAbaPreferida() {
    try {
        const saved = localStorage.getItem(ABA_STORAGE_KEY);
        if (saved === 'gastos' || saved === 'poupanca') return saved;
    } catch (_) {
        /* ignore */
    }
    return 'gastos';
}

function parseMoneyInput(value) {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const normalized = String(value)
        .replace(/R\$\s?/gi, '')
        .replace(/\s/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
    const n = Number.parseFloat(normalized);
    return Number.isFinite(n) ? n : null;
}

function MetasPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [abaAtiva, setAbaAtiva] = useState(loadAbaPreferida);
    const [metas, setMetas] = useState([]);
    const [metasPoupanca, setMetasPoupanca] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [contas, setContas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingPoupanca, setLoadingPoupanca] = useState(true);
    const [loadingContas, setLoadingContas] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [editingMeta, setEditingMeta] = useState(null);
    const [editingPoupanca, setEditingPoupanca] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalPoupancaOpen, setModalPoupancaOpen] = useState(false);
    const [aporteOpen, setAporteOpen] = useState(false);
    const [aporteMeta, setAporteMeta] = useState(null);
    const [resgateOpen, setResgateOpen] = useState(false);
    const [resgateMeta, setResgateMeta] = useState(null);
    const [mesAnoSelecionado, setMesAnoSelecionado] = useState(dayjs());
    const [regime, setRegime] = useState(loadRegimePreferido);
    const [form] = Form.useForm();
    const [formPoupanca] = Form.useForm();
    const [formAporte] = Form.useForm();
    const [formResgate] = Form.useForm();
    const [sugestaoReserva, setSugestaoReserva] = useState(null);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMeta, setDrawerMeta] = useState(null);
    const [detalheLoading, setDetalheLoading] = useState(false);
    const [detalhe, setDetalhe] = useState(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const carregarGastos = useCallback(async () => {
        try {
            setLoading(true);
            const ano = mesAnoSelecionado.year();
            const mes = mesAnoSelecionado.month() + 1;

            const [metasData, categoriasData] = await Promise.all([
                getMetasComProgresso(ano, mes, regime),
                getCategorias(),
            ]);

            setMetas(metasData);
            setCategorias(categoriasData.filter((c) => c.tipo === 'despesa'));
            setError(null);
        } catch (err) {
            console.error('Falha ao carregar dados', err);
            setError(getErrorMessage(err, 'Não foi possível carregar os dados. Tente novamente mais tarde.'));
        } finally {
            setLoading(false);
        }
    }, [mesAnoSelecionado, regime]);

    const carregarContas = useCallback(async () => {
        try {
            setLoadingContas(true);
            const contasData = await getContas({ status: 'ATIVO' });
            setContas(asList(contasData));
        } catch (err) {
            console.error('Falha ao carregar contas', err);
            setContas([]);
            notification.error({
                message: 'Contas',
                description: getErrorMessage(err, 'Não foi possível carregar suas contas.'),
            });
        } finally {
            setLoadingContas(false);
        }
    }, []);

    const carregarPoupanca = useCallback(async () => {
        try {
            setLoadingPoupanca(true);
            // Contas em paralelo, mas independentes: falha de metas não deixa o select vazio
            const [metasResult, contasResult] = await Promise.allSettled([
                getMetasPoupancaComProgresso(),
                getContas({ status: 'ATIVO' }),
            ]);

            if (metasResult.status === 'fulfilled') {
                setMetasPoupanca(asList(metasResult.value));
                setError(null);
            } else {
                console.error('Falha ao carregar metas de poupança', metasResult.reason);
                setMetasPoupanca([]);
                setError(getErrorMessage(metasResult.reason, 'Não foi possível carregar as metas de poupança.'));
            }

            if (contasResult.status === 'fulfilled') {
                setContas(asList(contasResult.value));
            } else {
                console.error('Falha ao carregar contas', contasResult.reason);
                setContas([]);
            }
        } catch (err) {
            console.error('Falha ao carregar aba Juntar dinheiro', err);
            setError(getErrorMessage(err, 'Não foi possível carregar as metas de poupança.'));
        } finally {
            setLoadingPoupanca(false);
        }
    }, []);

    useEffect(() => {
        if (abaAtiva === 'gastos') {
            carregarGastos();
        }
    }, [abaAtiva, carregarGastos]);

    useEffect(() => {
        if (abaAtiva === 'poupanca') {
            carregarPoupanca();
        }
    }, [abaAtiva, carregarPoupanca]);

    const resumo = useMemo(() => {
        const totalMetas = metas.reduce((acc, m) => acc + Number(m.valor || 0), 0);
        const totalGasto = metas.reduce((acc, m) => acc + Number(m.valor_gasto || 0), 0);
        const estouradas = metas.filter((m) => m.estourou || Number(m.valor_gasto) > Number(m.valor)).length;
        const percentualGeral = totalMetas > 0 ? (totalGasto / totalMetas) * 100 : 0;
        return { totalMetas, totalGasto, estouradas, percentualGeral, qtd: metas.length };
    }, [metas]);

    const resumoPoupanca = useMemo(() => {
        const totalAlvo = metasPoupanca.reduce((acc, m) => acc + Number(m.valor_alvo || 0), 0);
        const totalAtual = metasPoupanca.reduce((acc, m) => acc + Number(m.valor_atual || 0), 0);
        const atingidas = metasPoupanca.filter((m) => m.atingida).length;
        const percentualGeral = totalAlvo > 0 ? (totalAtual / totalAlvo) * 100 : 0;
        return { totalAlvo, totalAtual, atingidas, percentualGeral, qtd: metasPoupanca.length };
    }, [metasPoupanca]);

    const categoriasDisponiveis = categorias.filter(
        (cat) => !metas.some((meta) => meta.categoria.id === cat.id) || editingMeta?.categoria?.id === cat.id
    );

    // Caixinhas: várias metas podem usar a mesma conta
    const contasParaSelect = contas;

    const handleAbaChange = (key) => {
        setAbaAtiva(key);
        try {
            localStorage.setItem(ABA_STORAGE_KEY, key);
        } catch (_) {
            /* ignore */
        }
    };

    const handleRegimeChange = (value) => {
        setRegime(value);
        try {
            localStorage.setItem(REGIME_STORAGE_KEY, value);
        } catch (_) {
            /* ignore */
        }
    };

    const openCreateModal = () => {
        setEditingMeta(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEditModal = (meta) => {
        setEditingMeta(meta);
        form.setFieldsValue({
            categoria_id: meta.categoria.id,
            valor: Number(meta.valor),
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingMeta(null);
        form.resetFields();
    };

    const openCreatePoupanca = (tipoInicial = TIPO_OBJETIVO) => {
        setEditingPoupanca(null);
        formPoupanca.resetFields();
        formPoupanca.setFieldsValue({
            tipo: tipoInicial,
            meses_cobertura: tipoInicial === TIPO_RESERVA ? 6 : undefined,
        });
        setSugestaoReserva(null);
        setModalPoupancaOpen(true);
        if (contas.length === 0) {
            carregarContas();
        }
        if (tipoInicial === TIPO_RESERVA) {
            carregarSugestao(6);
        }
    };

    const openEditPoupanca = (meta) => {
        setEditingPoupanca(meta);
        formPoupanca.setFieldsValue({
            nome: meta.nome,
            tipo: meta.tipo,
            conta_id: meta.conta?.id,
            valor_alvo: Number(meta.valor_alvo),
            data_limite: meta.data_limite ? dayjs(meta.data_limite) : null,
            meses_cobertura: meta.meses_cobertura || undefined,
        });
        setSugestaoReserva(null);
        setModalPoupancaOpen(true);
        if (contas.length === 0) {
            carregarContas();
        }
        if (meta.tipo === TIPO_RESERVA && meta.meses_cobertura) {
            carregarSugestao(meta.meses_cobertura);
        }
    };

    const closeModalPoupanca = () => {
        setModalPoupancaOpen(false);
        setEditingPoupanca(null);
        formPoupanca.resetFields();
        setSugestaoReserva(null);
    };

    // Prefill a partir do simulador / deep link (?aba=poupanca&criar=1&valor_alvo=&data_limite=)
    useEffect(() => {
        const aba = searchParams.get('aba');
        if (aba === 'poupanca' || aba === 'gastos') {
            setAbaAtiva(aba);
            try {
                localStorage.setItem(ABA_STORAGE_KEY, aba);
            } catch (_) {
                /* ignore */
            }
        }
        const criar = searchParams.get('criar');
        if (criar !== '1') return;

        const valorAlvoParam = searchParams.get('valor_alvo');
        const dataLimiteParam = searchParams.get('data_limite');
        const valorAlvo = valorAlvoParam != null ? Number(valorAlvoParam) : null;

        setAbaAtiva('poupanca');
        setEditingPoupanca(null);
        formPoupanca.resetFields();
        formPoupanca.setFieldsValue({
            tipo: TIPO_OBJETIVO,
            valor_alvo: Number.isFinite(valorAlvo) && valorAlvo > 0 ? valorAlvo : undefined,
            data_limite: dataLimiteParam ? dayjs(dataLimiteParam) : null,
        });
        setSugestaoReserva(null);
        setModalPoupancaOpen(true);
        if (contas.length === 0) {
            carregarContas();
        }

        const next = new URLSearchParams(searchParams);
        next.delete('criar');
        next.delete('valor_alvo');
        next.delete('data_limite');
        setSearchParams(next, { replace: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    const carregarSugestao = async (meses) => {
        try {
            const data = await getSugestaoReserva(meses || 6);
            setSugestaoReserva(data);
        } catch (err) {
            console.error('Falha ao carregar sugestão de reserva', err);
            setSugestaoReserva(null);
        }
    };

    const openAporte = (meta) => {
        setAporteMeta(meta);
        formAporte.resetFields();
        formAporte.setFieldsValue({
            data: dayjs(),
            descricao: `Aporte: ${meta.nome}`,
        });
        setAporteOpen(true);
        if (contas.length === 0) {
            carregarContas();
        }
    };

    const closeAporte = () => {
        setAporteOpen(false);
        setAporteMeta(null);
        formAporte.resetFields();
    };

    const openResgate = (meta) => {
        setResgateMeta(meta);
        formResgate.resetFields();
        formResgate.setFieldsValue({
            data: dayjs(),
            descricao: `Resgate: ${meta.nome}`,
            valor: Number(meta.valor_alocado || meta.valor_atual || 0) || undefined,
        });
        setResgateOpen(true);
        if (contas.length === 0) {
            carregarContas();
        }
    };

    const closeResgate = () => {
        setResgateOpen(false);
        setResgateMeta(null);
        formResgate.resetFields();
    };

    const handleDelete = async (metaId) => {
        try {
            await deleteMeta(metaId);
            notification.success({
                message: 'Meta desativada',
                description: 'A meta foi desativada e não aparecerá mais nesta tela.',
            });
            if (drawerMeta?.id === metaId) {
                setDrawerOpen(false);
                setDrawerMeta(null);
                setDetalhe(null);
            }
            await carregarGastos();
        } catch (err) {
            console.error('Falha ao desativar meta', err);
            notification.error({
                message: 'Erro ao desativar',
                description: getErrorMessage(err, 'Não foi possível desativar a meta. Tente novamente.'),
            });
        }
    };

    const handleDeletePoupanca = async (metaId) => {
        try {
            await deleteMetaPoupanca(metaId);
            notification.success({
                message: 'Meta desativada',
                description: 'A meta de poupança foi desativada.',
            });
            await carregarPoupanca();
        } catch (err) {
            notification.error({
                message: 'Erro ao desativar',
                description: getErrorMessage(err, 'Não foi possível desativar a meta.'),
            });
        }
    };

    const onFinish = async (values) => {
        const payload = {
            categoria_id: values.categoria_id,
            valor: values.valor,
        };

        try {
            setSaving(true);
            await createOrUpdateMeta(editingMeta?.id || null, payload);
            notification.success({
                message: editingMeta ? 'Meta atualizada' : 'Meta criada',
                description: editingMeta
                    ? 'O limite da categoria foi atualizado.'
                    : 'Sua nova meta orçamentária está ativa.',
            });
            closeModal();
            await carregarGastos();
        } catch (err) {
            notification.error({
                message: 'Erro ao salvar',
                description: getErrorMessage(err, 'Não foi possível salvar a meta.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const onFinishPoupanca = async (values) => {
        const valorAlvo = parseMoneyInput(values.valor_alvo);
        if (valorAlvo == null || valorAlvo <= 0) {
            message.error('Informe um valor alvo maior que zero.');
            return;
        }
        if (!values.nome || !String(values.nome).trim()) {
            message.error('Informe um nome para o objetivo.');
            return;
        }
        if (!values.conta_id) {
            message.error('Selecione a conta vinculada.');
            return;
        }
        if (!values.tipo) {
            message.error('Selecione o tipo da meta.');
            return;
        }
        if (values.tipo === TIPO_RESERVA && (!values.meses_cobertura || values.meses_cobertura < 1)) {
            message.error('Informe quantos meses de cobertura.');
            return;
        }

        const payload = {
            nome: String(values.nome).trim(),
            tipo: values.tipo,
            conta_id: values.conta_id,
            valor_alvo: valorAlvo,
            data_limite:
                values.tipo === TIPO_OBJETIVO && values.data_limite
                    ? values.data_limite.format('YYYY-MM-DD')
                    : null,
            meses_cobertura: values.tipo === TIPO_RESERVA ? values.meses_cobertura : null,
        };

        try {
            setSaving(true);
            await createOrUpdateMetaPoupanca(editingPoupanca?.id || null, payload);
            message.success(editingPoupanca ? 'Meta atualizada.' : 'Meta criada com sucesso.');
            notification.success({
                message: editingPoupanca ? 'Meta atualizada' : 'Meta criada',
                description:
                    values.tipo === TIPO_RESERVA
                        ? 'Sua reserva de emergência está ativa. Use “Registrar aporte” para guardar dinheiro.'
                        : 'Seu objetivo está ativo. Use “Registrar aporte” para avançar.',
            });
            closeModalPoupanca();
            await carregarPoupanca();
        } catch (err) {
            console.error('Falha ao salvar meta de poupança', err, payload);
            const desc = getErrorMessage(err, 'Não foi possível salvar a meta de poupança.');
            message.error(desc, 8);
            notification.error({
                message: 'Erro ao salvar',
                description: desc,
                duration: 8,
            });
        } finally {
            setSaving(false);
        }
    };

    const submitPoupanca = async () => {
        try {
            let values = formPoupanca.getFieldsValue(true);
            try {
                const core = await formPoupanca.validateFields(['tipo', 'nome', 'conta_id', 'valor_alvo']);
                values = { ...values, ...core };
            } catch (validationError) {
                values = {
                    ...values,
                    ...(validationError?.values || {}),
                };
            }
            await onFinishPoupanca(values);
        } catch (err) {
            console.error('submitPoupanca', err);
            message.error(getErrorMessage(err, 'Não foi possível criar o objetivo. Tente novamente.'));
        }
    };

    const onFinishAporte = async (values) => {
        if (!aporteMeta) return;
        try {
            setSaving(true);
            await registrarAporteMetaPoupanca(aporteMeta.id, {
                conta_origem_id: values.conta_origem_id,
                valor: values.valor,
                data: values.data ? values.data.format('YYYY-MM-DD') : undefined,
                descricao: values.descricao,
            });
            notification.success({
                message: 'Aporte registrado',
                description: `Transferência de ${formatBRL(values.valor)} para ${aporteMeta.conta?.nome || 'a conta da meta'} aparecerá no extrato.`,
            });
            closeAporte();
            await carregarPoupanca();
        } catch (err) {
            notification.error({
                message: 'Erro no aporte',
                description: getErrorMessage(err, 'Não foi possível registrar o aporte.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const onFinishResgate = async (values) => {
        if (!resgateMeta) return;
        try {
            setSaving(true);
            await registrarResgateMetaPoupanca(resgateMeta.id, {
                conta_destino_id: values.conta_destino_id,
                valor: values.valor,
                data: values.data ? values.data.format('YYYY-MM-DD') : undefined,
                descricao: values.descricao,
            });
            notification.success({
                message: 'Resgate registrado',
                description: `${formatBRL(values.valor)} saiu da caixinha e foi para a conta escolhida.`,
            });
            closeResgate();
            await carregarPoupanca();
        } catch (err) {
            notification.error({
                message: 'Erro no resgate',
                description: getErrorMessage(err, 'Não foi possível registrar o resgate.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const openDetalhe = async (meta) => {
        setDrawerMeta(meta);
        setDrawerOpen(true);
        setDetalhe(null);
        setDetalheLoading(true);
        try {
            const ano = mesAnoSelecionado.year();
            const mes = mesAnoSelecionado.month() + 1;
            const data = await getDetalheMetaProgresso(ano, mes, meta.categoria.id, regime);
            setDetalhe(data);
        } catch (err) {
            console.error('Falha ao carregar detalhe da meta', err);
            notification.error({
                message: 'Erro ao carregar lançamentos',
                description: getErrorMessage(err, 'Não foi possível carregar os lançamentos desta meta.'),
            });
        } finally {
            setDetalheLoading(false);
        }
    };

    if (error && abaAtiva === 'gastos' && !loading && metas.length === 0) {
        return <Alert message="Erro" description={error} type="error" showIcon style={{ margin: '1rem' }} />;
    }

    const renderAbaGastos = () => (
        <>
            <Row justify="space-between" align="top" gutter={[16, 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
                <Col xs={24} md={16}>
                    <Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 560 }}>
                        Defina um limite mensal por categoria e acompanhe se está dentro do orçamento.
                    </Paragraph>
                </Col>
                <Col xs={24} md={8} style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                    <Button type="primary" icon={<PlusOutlined />} size="large" block={isMobile} onClick={openCreateModal}>
                        Nova meta
                    </Button>
                </Col>
            </Row>

            <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} lg={14}>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Space wrap align="center">
                                <Text strong>Como contar os gastos</Text>
                                <Tooltip title={REGIME_HELP[regime]}>
                                    <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                                </Tooltip>
                            </Space>
                            <Segmented
                                block={isMobile}
                                value={regime}
                                options={REGIME_OPTIONS}
                                onChange={handleRegimeChange}
                            />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {REGIME_HELP[regime]}
                            </Text>
                        </Space>
                    </Col>
                    <Col xs={24} lg={10} style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
                        <div style={{ width: isMobile ? '100%' : 'auto' }}>
                            <SeletorDeMes
                                value={mesAnoSelecionado.toDate()}
                                onChange={(newDate) => setMesAnoSelecionado(dayjs(newDate))}
                            />
                        </div>
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 50 }}>
                    <div className="brand-skeleton" style={{ padding: 24 }} aria-busy="true" aria-label="Carregando">
  <div className="brand-skeleton-line brand-skeleton-line--lg" />
  <div className="brand-skeleton-line brand-skeleton-line--md" />
  <div className="brand-skeleton-line" />
  <div className="brand-skeleton-line brand-skeleton-line--sm" />
</div>
                </div>
            ) : (
                <>
                    {metas.length > 0 && (
                        <Card size="small" style={{ marginBottom: 16 }} title={`Resumo de ${mesAnoSelecionado.format('MMMM / YYYY')}`}>
                            <Row gutter={[16, 16]}>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Metas" value={resumo.qtd} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Total das metas" value={formatBRL(resumo.totalMetas)} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Total gasto"
                                        value={formatBRL(resumo.totalGasto)}
                                        valueStyle={{ color: '#D64545' }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Uso geral"
                                        value={resumo.percentualGeral}
                                        precision={0}
                                        suffix="%"
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {resumo.estouradas > 0
                                            ? `${resumo.estouradas} estourou${resumo.estouradas > 1 ? 'ram' : ''}`
                                            : 'Nenhuma estourada'}
                                    </Text>
                                </Col>
                            </Row>
                        </Card>
                    )}

                    <Row gutter={[16, 16]}>
                        {metas.length > 0 ? (
                            metas.map((meta) => {
                                const percentual = parseFloat(meta.percentual_gasto) || 0;
                                const restante = Number(meta.valor_restante ?? Number(meta.valor) - Number(meta.valor_gasto));
                                const statusMeta = getStatusMeta(percentual);
                                const estourou = meta.estourou || restante < 0;

                                return (
                                    <Col xs={24} sm={12} md={8} lg={6} key={meta.id}>
                                        <Card
                                            hoverable
                                            title={meta.categoria.nome}
                                            extra={<Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
                                            onClick={() => openDetalhe(meta)}
                                            actions={[
                                                <Tooltip title="Ver lançamentos" key="view">
                                                    <Button
                                                        type="text"
                                                        icon={<EyeOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openDetalhe(meta);
                                                        }}
                                                    >
                                                        Detalhe
                                                    </Button>
                                                </Tooltip>,
                                                <Button
                                                    type="text"
                                                    icon={<EditOutlined />}
                                                    key="edit"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditModal(meta);
                                                    }}
                                                >
                                                    Editar
                                                </Button>,
                                                <Popconfirm
                                                    title="Desativar meta?"
                                                    description="Essa ação não poderá ser desfeita."
                                                    onConfirm={() => handleDelete(meta.id)}
                                                    okText="Sim"
                                                    cancelText="Não"
                                                    key="delete"
                                                >
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Excluir
                                                    </Button>
                                                </Popconfirm>,
                                            ]}
                                        >
                                            <div style={{ textAlign: 'center' }}>
                                                <Progress
                                                    type="circle"
                                                    percent={Math.min(percentual, 100)}
                                                    strokeColor={getProgressStroke(percentual)}
                                                    format={() => `${percentual.toFixed(0)}%`}
                                                    width={isMobile ? 100 : 120}
                                                />
                                                <div
                                                    style={{
                                                        marginTop: '1.25rem',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        gap: 8,
                                                        borderTop: '1px solid #f0f0f0',
                                                        paddingTop: '0.75rem',
                                                    }}
                                                >
                                                    <Statistic
                                                        title="Gasto"
                                                        value={formatBRL(meta.valor_gasto)}
                                                        valueStyle={{ fontSize: 15, color: '#D64545' }}
                                                    />
                                                    <Statistic
                                                        title="Meta"
                                                        value={formatBRL(meta.valor)}
                                                        valueStyle={{ fontSize: 15, color: '#2F9E6F' }}
                                                    />
                                                </div>
                                                <div style={{ marginTop: 12, textAlign: 'left' }}>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        {estourou ? 'Estourou em' : 'Restante'}
                                                    </Text>
                                                    <div
                                                        style={{
                                                            fontWeight: 600,
                                                            color: estourou ? '#D64545' : '#2F9E6F',
                                                        }}
                                                    >
                                                        {formatBRL(Math.abs(restante))}
                                                    </div>
                                                    <Progress
                                                        percent={Math.min(percentual, 100)}
                                                        showInfo={false}
                                                        size="small"
                                                        strokeColor={getProgressStroke(percentual)}
                                                        style={{ marginTop: 8, marginBottom: 0 }}
                                                    />
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })
                        ) : (
                            <Col span={24}>
                                <EmptyStateGuide
                                    title="Nenhuma meta definida."
                                    description="Crie limites de gasto por categoria para acompanhar seu orçamento mês a mês."
                                    actionLabel="Criar primeira meta"
                                    onAction={openCreateModal}
                                />
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </>
    );

    const renderAbaPoupanca = () => (
        <>
            <Row justify="space-between" align="top" gutter={[16, 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
                <Col xs={24} md={14}>
                    <Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 560 }}>
                        Cada objetivo é uma caixinha com valor próprio. Várias metas podem usar a mesma conta.
                        Aportes e resgates são sempre manuais — saques na conta sem resgate na meta não alteram o progresso.
                    </Paragraph>
                </Col>
                <Col xs={24} md={10} style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                    <Button
                        block={isMobile}
                        onClick={() => openCreatePoupanca(TIPO_RESERVA)}
                        disabled={metasPoupanca.some((m) => m.tipo === TIPO_RESERVA)}
                    >
                        Reserva de emergência
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} block={isMobile} onClick={() => openCreatePoupanca(TIPO_OBJETIVO)}>
                        Novo objetivo
                    </Button>
                </Col>
            </Row>

            {loadingPoupanca ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 50 }}>
                    <div className="brand-skeleton" style={{ padding: 24 }} aria-busy="true" aria-label="Carregando">
  <div className="brand-skeleton-line brand-skeleton-line--lg" />
  <div className="brand-skeleton-line brand-skeleton-line--md" />
  <div className="brand-skeleton-line" />
  <div className="brand-skeleton-line brand-skeleton-line--sm" />
</div>
                </div>
            ) : (
                <>
                    {metasPoupanca.length > 0 && (
                        <Card size="small" style={{ marginBottom: 16 }} title="Resumo">
                            <Row gutter={[16, 16]}>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Objetivos" value={resumoPoupanca.qtd} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic title="Total alvo" value={formatBRL(resumoPoupanca.totalAlvo)} />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Já guardado"
                                        value={formatBRL(resumoPoupanca.totalAtual)}
                                        valueStyle={{ color: '#2F9E6F' }}
                                    />
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Progresso geral"
                                        value={resumoPoupanca.percentualGeral}
                                        precision={0}
                                        suffix="%"
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {resumoPoupanca.atingidas > 0
                                            ? `${resumoPoupanca.atingidas} atingida${resumoPoupanca.atingidas > 1 ? 's' : ''}`
                                            : 'Nenhuma atingida ainda'}
                                    </Text>
                                </Col>
                            </Row>
                        </Card>
                    )}

                    <Row gutter={[16, 16]}>
                        {metasPoupanca.length > 0 ? (
                            metasPoupanca.map((meta) => {
                                const percentual = parseFloat(meta.percentual) || 0;
                                const statusMeta = getStatusPoupanca(percentual, meta.atingida);
                                const isReserva = meta.tipo === TIPO_RESERVA;

                                return (
                                    <Col xs={24} sm={12} md={8} lg={6} key={meta.id}>
                                        <Card
                                            title={meta.nome}
                                            extra={<Tag color={statusMeta.color}>{statusMeta.label}</Tag>}
                                            actions={[
                                                <Button
                                                    type="text"
                                                    icon={<SwapOutlined />}
                                                    key="aporte"
                                                    onClick={() => openAporte(meta)}
                                                >
                                                    Aporte
                                                </Button>,
                                                <Button
                                                    type="text"
                                                    key="resgate"
                                                    disabled={!(Number(meta.valor_alocado ?? meta.valor_atual) > 0)}
                                                    onClick={() => openResgate(meta)}
                                                >
                                                    Resgatar
                                                </Button>,
                                                <Button
                                                    type="text"
                                                    icon={<EditOutlined />}
                                                    key="edit"
                                                    onClick={() => openEditPoupanca(meta)}
                                                >
                                                    Editar
                                                </Button>,
                                                <Popconfirm
                                                    title="Desativar meta?"
                                                    description="O valor alocado na caixinha deixa de ser acompanhado; a conta permanece."
                                                    onConfirm={() => handleDeletePoupanca(meta.id)}
                                                    okText="Sim"
                                                    cancelText="Não"
                                                    key="delete"
                                                >
                                                    <Button type="text" danger icon={<DeleteOutlined />}>
                                                        Excluir
                                                    </Button>
                                                </Popconfirm>,
                                            ]}
                                        >
                                            <div style={{ textAlign: 'center' }}>
                                                {isReserva && (
                                                    <Tag color="gold" style={{ marginBottom: 12 }}>
                                                        Reserva · {meta.meses_cobertura || '—'} meses
                                                    </Tag>
                                                )}
                                                <Progress
                                                    type="circle"
                                                    percent={Math.min(percentual, 100)}
                                                    strokeColor={getPoupancaStroke(percentual)}
                                                    format={() => `${percentual.toFixed(0)}%`}
                                                    width={isMobile ? 100 : 120}
                                                />
                                                <div
                                                    style={{
                                                        marginTop: '1.25rem',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        gap: 8,
                                                        borderTop: '1px solid #f0f0f0',
                                                        paddingTop: '0.75rem',
                                                    }}
                                                >
                                                    <Statistic
                                                        title="Guardado"
                                                        value={formatBRL(meta.valor_alocado ?? meta.valor_atual)}
                                                        valueStyle={{ fontSize: 15, color: '#2F9E6F' }}
                                                    />
                                                    <Statistic
                                                        title="Alvo"
                                                        value={formatBRL(meta.valor_alvo)}
                                                        valueStyle={{ fontSize: 15 }}
                                                    />
                                                </div>
                                                <div style={{ marginTop: 12, textAlign: 'left' }}>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        Conta: {meta.conta?.nome || '—'}
                                                    </Text>
                                                    {meta.conta?.saldo_livre != null && (
                                                        <div>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                Livre na conta: {formatBRL(meta.conta.saldo_livre)}
                                                            </Text>
                                                        </div>
                                                    )}
                                                    {meta.data_limite && (
                                                        <div>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                Prazo: {formatDataCurta(meta.data_limite)}
                                                            </Text>
                                                        </div>
                                                    )}
                                                    {isReserva && meta.meses_cobertos != null && (
                                                        <div style={{ marginTop: 4 }}>
                                                            <Text strong style={{ color: '#50B0A0' }}>
                                                                {Number(meta.meses_cobertos).toFixed(1)} meses cobertos
                                                            </Text>
                                                            {meta.media_despesas_mensais != null && (
                                                                <div>
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                                        Média de despesas: {formatBRL(meta.media_despesas_mensais)}/mês
                                                                    </Text>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {!meta.atingida && (
                                                        <div style={{ marginTop: 8 }}>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>Falta</Text>
                                                            <div style={{ fontWeight: 600 }}>{formatBRL(meta.valor_faltante)}</div>
                                                        </div>
                                                    )}
                                                    <Space direction="vertical" style={{ width: '100%', marginTop: 12 }} size={8}>
                                                        <Button
                                                            type="primary"
                                                            block
                                                            icon={<SwapOutlined />}
                                                            onClick={() => openAporte(meta)}
                                                        >
                                                            Registrar aporte
                                                        </Button>
                                                        <Button
                                                            block
                                                            disabled={!(Number(meta.valor_alocado ?? meta.valor_atual) > 0)}
                                                            onClick={() => openResgate(meta)}
                                                        >
                                                            Resgatar
                                                        </Button>
                                                    </Space>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                );
                            })
                        ) : (
                            <Col span={24}>
                                <EmptyStateGuide
                                    title="Nenhum objetivo de poupança."
                                    description="Crie um objetivo (viagem, carro…) ou uma reserva de emergência. Você decide cada aporte."
                                    actionLabel="Criar primeiro objetivo"
                                    onAction={() => openCreatePoupanca(TIPO_OBJETIVO)}
                                />
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </>
    );

    return (
        <Content style={{ padding: isMobile ? 0 : '2rem', width: '100%' }}>
            <Title level={isMobile ? 3 : 2} style={{ margin: 0, marginBottom: 8 }}>
                Metas
            </Title>

            <Tabs
                activeKey={abaAtiva}
                onChange={handleAbaChange}
                type="card"
                className="sa-page-tabs"
                style={{ marginBottom: 8 }}
                items={[
                    { key: 'gastos', label: 'Limitar gastos', children: renderAbaGastos() },
                    { key: 'poupanca', label: 'Juntar dinheiro', children: renderAbaPoupanca() },
                ]}
            />

            {isMobile ? (
                <MobileLaunchSheet
                    open={modalOpen}
                    title={editingMeta ? 'Editar meta' : 'Nova meta'}
                    onCancel={closeModal}
                    onSave={() => form.submit()}
                    saveLabel={editingMeta ? 'Atualizar' : 'Criar'}
                    saveLoading={saving}
                >
                    <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                        Uma meta é um teto mensal de gasto para uma categoria de despesa.
                    </Paragraph>
                    <Form form={form} layout="vertical" onFinish={onFinish} size="large">
                        <Form.Item
                            name="categoria_id"
                            label="Categoria"
                            rules={[{ required: true, message: 'Selecione a categoria!' }]}
                        >
                            <Select
                                placeholder="Selecione a categoria"
                                disabled={!!editingMeta}
                                showSearch
                                optionFilterProp="children"
                                size="large"
                            >
                                {(editingMeta
                                    ? [{ id: editingMeta.categoria.id, nome: editingMeta.categoria.nome }]
                                    : categoriasDisponiveis
                                ).map((cat) => (
                                    <Option key={cat.id} value={cat.id}>
                                        {cat.nome}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="valor"
                            label="Valor da meta"
                            rules={[{ required: true, message: 'Insira o valor da meta!' }]}
                            extra="Digite só os números; a máscara é aplicada automaticamente."
                        >
                            <InputNumber {...moneyInputNumberProps} />
                        </Form.Item>
                    </Form>
                </MobileLaunchSheet>
            ) : (
            <Modal
                title={editingMeta ? 'Editar meta' : 'Nova meta'}
                open={modalOpen}
                onCancel={closeModal}
                footer={null}
                destroyOnClose
            >
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Uma meta é um teto mensal de gasto para uma categoria de despesa.
                </Paragraph>
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Form.Item
                        name="categoria_id"
                        label="Categoria"
                        rules={[{ required: true, message: 'Selecione a categoria!' }]}
                    >
                        <Select
                            placeholder="Selecione a categoria"
                            disabled={!!editingMeta}
                            showSearch
                            optionFilterProp="children"
                            size="large"
                        >
                            {(editingMeta
                                ? [{ id: editingMeta.categoria.id, nome: editingMeta.categoria.nome }]
                                : categoriasDisponiveis
                            ).map((cat) => (
                                <Option key={cat.id} value={cat.id}>
                                    {cat.nome}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="valor"
                        label="Valor da meta"
                        rules={[{ required: true, message: 'Insira o valor da meta!' }]}
                        extra="Digite só os números; a máscara é aplicada automaticamente."
                    >
                        <InputNumber {...moneyInputNumberProps} />
                    </Form.Item>

                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={closeModal}>Cancelar</Button>
                        <Button type="primary" htmlType="submit" loading={saving} icon={editingMeta ? <SaveOutlined /> : <PlusOutlined />}>
                            {editingMeta ? 'Atualizar' : 'Criar meta'}
                        </Button>
                    </Space>
                </Form>
            </Modal>
            )}

            <Modal
                title={editingPoupanca ? 'Editar objetivo' : 'Novo objetivo de poupança'}
                open={modalPoupancaOpen}
                onCancel={closeModalPoupanca}
                destroyOnClose={false}
                forceRender
                {...(isMobile
                    ? getMobileFullscreenModalProps(true)
                    : { width: 520 })}
                footer={isMobile ? (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button key="cancel" onClick={closeModalPoupanca} disabled={saving} block size="large">
                            Cancelar
                        </Button>
                        <Button key="ok" type="primary" loading={saving} onClick={submitPoupanca} block size="large">
                            {editingPoupanca ? 'Atualizar' : 'Criar'}
                        </Button>
                    </div>
                ) : [
                    <Button key="cancel" onClick={closeModalPoupanca} disabled={saving}>
                        Cancelar
                    </Button>,
                    <Button key="ok" type="primary" loading={saving} onClick={submitPoupanca}>
                        {editingPoupanca ? 'Atualizar' : 'Criar'}
                    </Button>,
                ]}
            >
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Vincule a uma conta. O progresso usa o valor da caixinha (aportes), não o saldo bruto da conta.
                    Várias metas podem compartilhar a mesma conta.
                </Paragraph>
                <Form
                    form={formPoupanca}
                    layout="vertical"
                    requiredMark
                    onValuesChange={(changed) => {
                        if (changed.tipo === TIPO_RESERVA) {
                            const meses = formPoupanca.getFieldValue('meses_cobertura') || 6;
                            formPoupanca.setFieldsValue({ meses_cobertura: meses });
                            carregarSugestao(meses);
                        }
                        if (changed.tipo === TIPO_OBJETIVO) {
                            setSugestaoReserva(null);
                        }
                        if (changed.meses_cobertura != null) {
                            carregarSugestao(changed.meses_cobertura);
                        }
                    }}
                >
                    <Form.Item name="tipo" label="Tipo" rules={[{ required: true, message: 'Selecione o tipo!' }]}>
                        <Segmented
                            block
                            options={[
                                { label: 'Objetivo', value: TIPO_OBJETIVO },
                                { label: 'Reserva de emergência', value: TIPO_RESERVA },
                            ]}
                            disabled={!!editingPoupanca}
                        />
                    </Form.Item>

                    <Form.Item
                        name="nome"
                        label="Nome"
                        rules={[{ required: true, message: 'Informe um nome!' }]}
                    >
                        <Input placeholder="Ex.: Viagem, Reserva de emergência" size="large" maxLength={100} />
                    </Form.Item>

                    <Form.Item
                        name="conta_id"
                        label="Conta vinculada"
                        rules={[{ required: true, message: 'Selecione a conta!' }]}
                        extra={
                            contas.length === 0 && !loadingContas ? (
                                <Text type="secondary">
                                    Nenhuma conta ativa.{' '}
                                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/contas')}>
                                        Criar conta
                                    </Button>
                                </Text>
                            ) : (
                                'O saldo desta conta conta como progresso da meta.'
                            )
                        }
                    >
                        <Select
                            placeholder={loadingContas ? 'Carregando contas...' : 'Selecione a conta'}
                            showSearch
                            optionFilterProp="label"
                            size="large"
                            loading={loadingContas}
                            disabled={!!editingPoupanca || loadingContas}
                            notFoundContent={loadingContas ? 'Carregando...' : 'Nenhuma conta disponível'}
                            options={(contasParaSelect || []).map((c) => ({
                                value: c.id,
                                label: `${c.nome} (${labelTipoConta(c.tipo_conta)})`,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) => prev.tipo !== cur.tipo}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('tipo') === TIPO_RESERVA ? (
                                <Form.Item
                                    name="meses_cobertura"
                                    label="Meses de cobertura"
                                    rules={[{ required: true, message: 'Informe os meses!' }]}
                                    extra={
                                        sugestaoReserva
                                            ? `Sugestão com base na média de despesas (${formatBRL(sugestaoReserva.media_despesas_mensais)}/mês): ${formatBRL(sugestaoReserva.valor_sugerido)}`
                                            : 'Com base na média de despesas dos últimos meses.'
                                    }
                                >
                                    <InputNumber min={1} max={36} style={{ width: '100%' }} size="large" />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        name="valor_alvo"
                        label="Valor alvo"
                        rules={[{ required: true, message: 'Informe o valor alvo!' }]}
                        extra={
                            sugestaoReserva ? (
                                <Button
                                    type="link"
                                    size="small"
                                    htmlType="button"
                                    style={{ padding: 0 }}
                                    onClick={() =>
                                        formPoupanca.setFieldsValue({
                                            valor_alvo: Number(sugestaoReserva.valor_sugerido),
                                        })
                                    }
                                >
                                    Usar sugestão {formatBRL(sugestaoReserva.valor_sugerido)}
                                </Button>
                            ) : (
                                'Digite só os números; a máscara é aplicada automaticamente.'
                            )
                        }
                    >
                        <InputNumber {...moneyInputNumberProps} />
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.tipo !== cur.tipo}>
                        {({ getFieldValue }) =>
                            getFieldValue('tipo') === TIPO_OBJETIVO ? (
                                <Form.Item name="data_limite" label="Prazo (opcional)">
                                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" size="large" />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>
                </Form>
            </Modal>

            {isMobile ? (
                <MobileLaunchSheet
                    open={aporteOpen}
                    title={aporteMeta ? `Aporte · ${aporteMeta.nome}` : 'Registrar aporte'}
                    onCancel={closeAporte}
                    onSave={() => formAporte.submit()}
                    saveLabel="Confirmar"
                    saveLoading={saving}
                >
                    <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                        Você confirma a transferência agora. Ela aparece no extrato das duas contas — nada é automático.
                    </Paragraph>
                    <Form form={formAporte} layout="vertical" onFinish={onFinishAporte} size="large">
                        <Form.Item
                            name="conta_origem_id"
                            label="De qual conta?"
                            rules={[{ required: true, message: 'Selecione a conta de origem!' }]}
                        >
                            <Select
                                placeholder={loadingContas ? 'Carregando contas...' : 'Conta de origem'}
                                showSearch
                                optionFilterProp="label"
                                size="large"
                                loading={loadingContas}
                                options={contas
                                    .filter((c) => c.id !== aporteMeta?.conta?.id)
                                    .map((c) => ({
                                        value: c.id,
                                        label: `${c.nome} · ${formatBRL(c.saldo_atual)}`,
                                    }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="valor"
                            label="Valor do aporte"
                            rules={[{ required: true, message: 'Informe o valor!' }]}
                            extra="Digite só os números; a máscara é aplicada automaticamente."
                        >
                            <InputNumber {...moneyInputNumberProps} />
                        </Form.Item>
                        <Form.Item name="data" label="Data" rules={[{ required: true }]}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" size="large" />
                        </Form.Item>
                        <Form.Item name="descricao" label="Descrição">
                            <Input size="large" maxLength={255} />
                        </Form.Item>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={`Destino: ${aporteMeta?.conta?.nome || 'conta da meta'}`}
                        />
                    </Form>
                </MobileLaunchSheet>
            ) : (
            <Modal
                title={aporteMeta ? `Registrar aporte · ${aporteMeta.nome}` : 'Registrar aporte'}
                open={aporteOpen}
                onCancel={closeAporte}
                footer={null}
                destroyOnClose
            >
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Você confirma a transferência agora. Ela aparece no extrato das duas contas — nada é automático.
                </Paragraph>
                <Form form={formAporte} layout="vertical" onFinish={onFinishAporte}>
                    <Form.Item
                        name="conta_origem_id"
                        label="De qual conta?"
                        rules={[{ required: true, message: 'Selecione a conta de origem!' }]}
                    >
                        <Select
                            placeholder={loadingContas ? 'Carregando contas...' : 'Conta de origem'}
                            showSearch
                            optionFilterProp="label"
                            size="large"
                            loading={loadingContas}
                            options={contas
                                .filter((c) => c.id !== aporteMeta?.conta?.id)
                                .map((c) => ({
                                    value: c.id,
                                    label: `${c.nome} · ${formatBRL(c.saldo_atual)}`,
                                }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="valor"
                        label="Valor do aporte"
                        rules={[{ required: true, message: 'Informe o valor!' }]}
                        extra="Digite só os números; a máscara é aplicada automaticamente."
                    >
                        <InputNumber {...moneyInputNumberProps} />
                    </Form.Item>
                    <Form.Item name="data" label="Data" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" size="large" />
                    </Form.Item>
                    <Form.Item name="descricao" label="Descrição">
                        <Input size="large" maxLength={255} />
                    </Form.Item>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={`Destino: ${aporteMeta?.conta?.nome || 'conta da meta'}`}
                    />
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={closeAporte}>Cancelar</Button>
                        <Button type="primary" htmlType="submit" loading={saving} icon={<SwapOutlined />}>
                            Confirmar aporte
                        </Button>
                    </Space>
                </Form>
            </Modal>
            )}

            {isMobile ? (
                <MobileLaunchSheet
                    open={resgateOpen}
                    title={resgateMeta ? `Resgatar · ${resgateMeta.nome}` : 'Resgatar'}
                    onCancel={closeResgate}
                    onSave={() => formResgate.submit()}
                    saveLabel="Confirmar"
                    saveLoading={saving}
                >
                    <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                        Você confirma a saída da caixinha agora. O valor vai para a conta escolhida e o progresso da meta diminui.
                    </Paragraph>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={`Disponível na caixinha: ${formatBRL(resgateMeta?.valor_alocado ?? resgateMeta?.valor_atual)}`}
                    />
                    <Form form={formResgate} layout="vertical" onFinish={onFinishResgate} size="large">
                        <Form.Item
                            name="conta_destino_id"
                            label="Para qual conta?"
                            rules={[{ required: true, message: 'Selecione a conta de destino!' }]}
                        >
                            <Select
                                placeholder={loadingContas ? 'Carregando contas...' : 'Conta de destino'}
                                showSearch
                                optionFilterProp="label"
                                size="large"
                                loading={loadingContas}
                                options={contas
                                    .filter((c) => c.id !== resgateMeta?.conta?.id)
                                    .map((c) => ({
                                        value: c.id,
                                        label: `${c.nome} · ${formatBRL(c.saldo_atual)}`,
                                    }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="valor"
                            label="Valor do resgate"
                            rules={[{ required: true, message: 'Informe o valor!' }]}
                            extra="Digite só os números; a máscara é aplicada automaticamente."
                        >
                            <InputNumber {...moneyInputNumberProps} />
                        </Form.Item>
                        <Form.Item name="data" label="Data" rules={[{ required: true }]}>
                            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" size="large" />
                        </Form.Item>
                        <Form.Item name="descricao" label="Descrição">
                            <Input size="large" maxLength={255} />
                        </Form.Item>
                    </Form>
                </MobileLaunchSheet>
            ) : (
            <Modal
                title={resgateMeta ? `Resgatar · ${resgateMeta.nome}` : 'Resgatar'}
                open={resgateOpen}
                onCancel={closeResgate}
                footer={null}
                destroyOnClose
            >
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Você confirma a saída da caixinha agora. O valor vai para a conta escolhida e o progresso da meta diminui.
                </Paragraph>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={`Disponível na caixinha: ${formatBRL(resgateMeta?.valor_alocado ?? resgateMeta?.valor_atual)}`}
                />
                <Form form={formResgate} layout="vertical" onFinish={onFinishResgate}>
                    <Form.Item
                        name="conta_destino_id"
                        label="Para qual conta?"
                        rules={[{ required: true, message: 'Selecione a conta de destino!' }]}
                    >
                        <Select
                            placeholder={loadingContas ? 'Carregando contas...' : 'Conta de destino'}
                            showSearch
                            optionFilterProp="label"
                            size="large"
                            loading={loadingContas}
                            options={contas
                                .filter((c) => c.id !== resgateMeta?.conta?.id)
                                .map((c) => ({
                                    value: c.id,
                                    label: `${c.nome} · ${formatBRL(c.saldo_atual)}`,
                                }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="valor"
                        label="Valor do resgate"
                        rules={[{ required: true, message: 'Informe o valor!' }]}
                        extra="Digite só os números; a máscara é aplicada automaticamente."
                    >
                        <InputNumber {...moneyInputNumberProps} />
                    </Form.Item>
                    <Form.Item name="data" label="Data" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" size="large" />
                    </Form.Item>
                    <Form.Item name="descricao" label="Descrição">
                        <Input size="large" maxLength={255} />
                    </Form.Item>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={closeResgate}>Cancelar</Button>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            Confirmar resgate
                        </Button>
                    </Space>
                </Form>
            </Modal>
            )}

            <Drawer
                title={drawerMeta ? `Lançamentos · ${drawerMeta.categoria.nome}` : 'Lançamentos'}
                open={drawerOpen}
                onClose={() => {
                    setDrawerOpen(false);
                    setDrawerMeta(null);
                    setDetalhe(null);
                }}
                width={isMobile ? '100%' : 440}
                destroyOnClose
                styles={{
                    body: { padding: 16, background: '#fff' },
                    header: { background: '#fff' },
                }}
            >
                {detalheLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin />
                    </div>
                ) : detalhe ? (
                    <>
                        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size={4}>
                            <Text type="secondary">{REGIME_HELP[regime]}</Text>
                            <Text>
                                Gasto no período: <Text strong>{formatBRL(detalhe.valor_gasto)}</Text>
                                {' · '}
                                Meta: <Text strong>{formatBRL(detalhe.valor_meta)}</Text>
                            </Text>
                        </Space>
                        <List
                            locale={{ emptyText: 'Nenhum lançamento neste mês para o modo selecionado.' }}
                            dataSource={detalhe.lancamentos || []}
                            renderItem={(item) => (
                                <List.Item
                                    style={{ alignItems: 'flex-start', gap: 12 }}
                                    extra={
                                        <Text strong style={{ color: '#D64545', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                            {formatBRL(item.valor)}
                                        </Text>
                                    }
                                >
                                    <List.Item.Meta
                                        style={{ minWidth: 0 }}
                                        title={
                                            <Space wrap size={[4, 4]}>
                                                <span style={{ wordBreak: 'break-word' }}>{item.descricao}</span>
                                                {item.projetado ? <Tag>Projetado</Tag> : null}
                                            </Space>
                                        }
                                        description={
                                            <>
                                                {formatDataCurta(item.data)}
                                                {' · '}
                                                {item.origem === 'cartao' ? 'Cartão' : 'Conta'}
                                                {item.origem_nome ? ` (${item.origem_nome})` : ''}
                                            </>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </>
                ) : (
                    <EmptyStateGuide
                        title="Não foi possível carregar os lançamentos."
                        description="Feche e tente novamente."
                    />
                )}
            </Drawer>
        </Content>
    );
}

export default MetasPage;
